// Formatting helpers shared across CI panels.
// Pure functions — depend on constants but never on React.
//
// These consolidate ~30 panels' worth of locally-redefined formatters. Where the
// old per-file copies genuinely differed (whole-dollar vs cents, comma grouping,
// signed vs unsigned, with-year vs without-year dates), each behaviour is kept as
// a distinctly-named export rather than collapsed — so migrating a panel is a
// rename, never a behaviour change. The leading "—" / null em-dash placeholders
// match what every call site already rendered for missing values.

import { MONTHS } from "./constants";

/* ----------------------------- money / rates ----------------------------- */

// "$1234.50" — two decimals, no grouping. The dominant CI money format; was
// redefined as both `fmtRate` and `fmtMoney` across panels with identical output.
export const fmtRate = (value) => (value == null ? "—" : `$${Number(value).toFixed(2)}`);

// "$1234" — whole dollars, no decimals. Used by the DOW heatmap, where cents add
// noise to the per-cell averages.
export const fmtRateWhole = (value) => (value == null ? "—" : `$${Number(value).toFixed(0)}`);

// "$1,234.50" — two decimals, comma-grouped (en-US). Was the `fmtMoney` in the
// revenue-opportunity panels, where headline totals warrant thousands separators.
export const fmtMoneyLocale = (value) => {
  if (value == null) return "—";
  return `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// "$1,234" / "$1,234.50" — comma-grouped, 0–2 decimals (drops trailing cents when
// whole). Was DemandIndexPanel's `fmtMoney`.
export const fmtMoneyCompact = (value) =>
  value == null ? "—" : `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// "+$1,234.50" / "−$1,234.50" — signed, comma-grouped. Note the minus sign is a
// real U+2212 minus, matching the original. Was RevenueOpportunityPanel.
export const fmtSignedMoney = (value) => {
  if (value == null) return "—";
  const num = Number(value);
  const sign = num > 0 ? "+" : num < 0 ? "−" : "";
  return `${sign}$${Math.abs(num).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// "+$1234.50" / "−$1234.50" — signed, two decimals, no grouping. Was the signed
// money helper in RateChangeFeedPanel (differs from fmtSignedMoney only in grouping).
export const fmtSignedRate = (value) => {
  if (value == null) return "—";
  const num = Number(value);
  return `${num > 0 ? "+" : num < 0 ? "−" : ""}$${Math.abs(num).toFixed(2)}`;
};

/* ----------------------------- percentages ------------------------------ */

// "12.3%" — one decimal. The standard CI percentage format.
//
// INPUT IS ALREADY A PERCENT: 27 → "27.0%". CI's backend sends percent-valued
// numbers (soldout_pct, client_premium_pct, weekend_premium_pct), so this does
// NOT multiply by 100.
//
// ⚠️ CONTRACT TRAP: this is NOT the same as the RATIO-input `fmtPercent` in
// src/components/shared/format.js and google-analytics/ (0.27 → "27.00%",
// which multiplies by 100). Same-looking name, opposite input. Passing a CI
// percent (27) to fmtPercent yields "2700.00%". If you ever need the shared
// module here, the percent-input equivalent is `fmtPctValue` in shared/format.js.
export const fmtPct = (value) => (value == null ? "—" : `${Number(value).toFixed(1)}%`);

// "+12.34%" / "12.34%" — signed (only when positive), two decimals. Was the
// `fmtPct` in RateChangeFeedPanel, where a leading + signals a rate increase.
export const fmtPctSigned = (value) => (value == null ? "—" : `${value > 0 ? "+" : ""}${Number(value).toFixed(2)}%`);

/* ----------------------------- counts ----------------------------------- */

// "1,234" — locale-grouped integer/decimal. Was `fmtNum` in the absorption panels.
export const fmtNum = (value) => (value == null ? "—" : Number(value).toLocaleString());

/* ----------------------------- dates ------------------------------------ */
// Backend dates are plain "YYYY-MM-DD" strings; we split rather than Date()-parse
// to avoid the UTC-to-local day shift.

// "Jun 15, 2026" — month, day, year.
export const fmtDate = (iso) => {
  if (!iso) return "—";
  const [year, month, day] = iso.split("-");
  return `${MONTHS[parseInt(month, 10) - 1]} ${parseInt(day, 10)}, ${year}`;
};

// "Jun 15" — month and day only, no year. Used where the year is implied by
// context (forward calendars, near-term pricing rows).
export const fmtDateShort = (iso) => {
  if (!iso) return "—";
  const [, month, day] = iso.split("-");
  return `${MONTHS[parseInt(month, 10) - 1]} ${parseInt(day, 10)}`;
};

// Whole-day span between two "YYYY-MM-DD" check-in / check-out strings (a stay's
// nights). Built on Date.UTC of the split parts — a pure day-count diff, so unlike
// the display formatters above it can't drift on a local-vs-UTC boundary. Returns
// null when either date is missing or unparseable. Some report RPCs (rate-calendar,
// market-position) send a server-computed `nights` field; others (demand-signals)
// send only the two dates, so this recovers the same value on the frontend.
export const nightsBetween = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return null;
  const [y1, m1, d1] = checkIn.split("-").map((n) => parseInt(n, 10));
  const [y2, m2, d2] = checkOut.split("-").map((n) => parseInt(n, 10));
  if ([y1, m1, d1, y2, m2, d2].some(Number.isNaN)) return null;
  const nights = Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
  return nights > 0 ? nights : null;
};
